import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Book as BookIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, getDocs, doc } from 'firebase/firestore'
import toast from 'react-hot-toast'
import BookCard from '../components/BookCard'
import BookDetail from './BookDetail'
import SpaceTheme from '../components/SpaceTheme'

const Reading = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [books, setBooks] = useState([])
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState('reading')
  const [bookData, setBookData] = useState(null)
  
  // userData prop'unun undefined olma durumunu handle et
  const safeUserData = userData || {}

  useEffect(() => {
    if (user && user.uid) {
      loadBooks()
    } else {
      setLoading(false)
    }
  }, [user, userData])

  const loadBooks = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const userLevel = safeUserData?.level || 'A1'
      
      // Firestore'dan seviyeye göre kitapları al
      const booksRef = collection(db, 'levels', userLevel, 'books')
      const booksSnapshot = await getDocs(booksRef)
      
      const booksData = []
      booksSnapshot.forEach((doc) => {
        const bookData = doc.data()
        booksData.push({
          id: doc.id,
          title: bookData.title || 'Başlıksız Kitap',
          content: bookData.content || '',
          imageUrl: bookData.imageUrl || null,
          imageResourceId: bookData.imageResourceId || null
        })
      })
      
      setBooks(booksData)
    } catch (error) {
      console.error('Error loading books:', error)
      setError('Kitaplar yüklenirken hata oluştu')
      toast.error('Kitaplar yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleBookClick = (book) => {
    // Kitap detay sayfasına yönlendir
    setCurrentPage('book-detail')
    setBookData(book)
  }

  const handleBackToReading = (data) => {
    if (data && data.page) {
      setCurrentPage(data.page)
    } else {
      setCurrentPage('reading')
    }
  }

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                console.log('Reading back button clicked, onBack:', onBack)
                if (onBack && typeof onBack === 'function') {
                  onBack()
                } else {
                  console.error('onBack is not a function:', onBack)
                }
              }}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Okuma
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Kitaplar yükleniyor...</Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  // Book Detail sayfasını göster
  if (currentPage === 'book-detail' && bookData) {
    return <BookDetail bookData={bookData} onBack={handleBackToReading} />
  }

  return (
    <SpaceTheme>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => {
              console.log('Reading main back button clicked, onBack:', onBack)
              if (onBack && typeof onBack === 'function') {
                onBack()
              } else {
                console.error('onBack is not a function:', onBack)
              }
            }}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Okuma
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Header Card */}
        <Card sx={{ 
          mx: 2, 
          mt: 2, 
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <BookIcon sx={{ fontSize: 56, color: '#1976d2', mb: 3 }} />
            <Typography variant="h3" sx={{ 
              color: '#1976d2', 
              fontWeight: 'bold',
              mb: 1
            }}>
              Okuma Metinleri
            </Typography>
            <Typography variant="h6" sx={{ color: '#1976d2', opacity: 0.8 }}>
              Seviyenize uygun İngilizce okuma metinleri
            </Typography>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 2, mx: 2 }}>
            {error}
          </Alert>
        )}

        {/* Books Grid */}
        {books.length > 0 ? (
          <Grid container spacing={2} sx={{ mt: 2, px: 2 }}>
            {books.map((book) => (
              <Grid item xs={12} sm={6} md={4} key={book.id}>
                <BookCard 
                  book={book} 
                  onClick={() => handleBookClick(book)}
                />
              </Grid>
            ))}
          </Grid>
                 ) : !loading && !error ? (
           <Card sx={{ 
             mt: 3, 
             mx: 2,
             backgroundColor: 'rgba(255, 255, 255, 0.95)',
             backdropFilter: 'blur(10px)',
             border: '1px solid rgba(255, 255, 255, 0.2)',
             boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
           }}>
             <CardContent sx={{ textAlign: 'center', py: 4 }}>
               <BookIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
               <Typography variant="h6" color="text.secondary" gutterBottom>
                 Henüz Okuma Metni Yok
               </Typography>
               <Typography variant="body2" color="text.secondary">
                 Bu seviye için henüz okuma metni eklenmemiş.
               </Typography>
             </CardContent>
           </Card>
        ) : null}
      </Container>
    </SpaceTheme>
  )
}

export default Reading
